import apiClient from 'helpers/apiService';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { Card, Col, Offcanvas, Row, Spinner } from 'react-bootstrap';
import ReactTextareaAutosize from 'react-textarea-autosize';
import Button from 'components/base/Button';
import { PromptLight, Message as MessageType } from './DataDefinitions';
import { useEffect, useRef, useState } from 'react';
import PromptsProvider, { useChatContext } from './PromptsProvider';
import React from 'react';
import classNames from 'classnames';
import Avatar from 'components/base/Avatar';
import ai from 'assets/img/th-each-ai/brain.png';
import DynamicIcon from 'components/theach/Icons';
import parse from 'html-react-parser';

interface PromptWidgetProps {
    prompt: PromptLight;
    showPromptDrawer: boolean;
    onClosePromptDrawer: () => void;
}

interface SampleQuestionsProps {
    questions: string[];
    onClickMessage: (question: string) => void; // Function to handle sending a question to AI
}


const getFilteredQuestions = (prompt: PromptLight): string[] => {
    // Create an array with questions you want to check
    const questions = [prompt.question1, prompt.question2, prompt.question3, prompt.question4];
    // Filter out null or empty strings
    const filteredQuestions = questions.filter((q) => q !== null && q !== '');
    return filteredQuestions;
};

const SampleQuestions = ({ questions, onClickMessage }: SampleQuestionsProps) => {
    const { t } = useTranslation();
    return (
        <div className="d-flex flex-column">

            {questions.length > 0 && <p className="mx-2 fs-8" style={{ fontStyle: 'italic' }}>{t("SampleQuestions.info")}</p>}
            {questions.map((question, index) => (
                <Card key={index} className='mb-2' style={{ cursor: 'pointer' }}>
                    <Card.Body>
                        <Card.Text onClick={() => onClickMessage(question)} className="fs-8" style={{ fontStyle: 'italic' }} > {question} </Card.Text>
                    </Card.Body>
                </Card>
            ))}
        </div>);
}

interface MessageProps {
    message: MessageType;
    showActions?: boolean;
}

const Message = ({ message, showActions = true }: MessageProps) => {

    const formatText = (text: string) => {
        return text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                <br />
            </React.Fragment>
        ));
    };

    return (
        <div className="d-flex chat-message mt-1">
            <div className={classNames('d-flex flex-1', { 'justify-content-end': message.type === 'sent' })}>
                <div className={classNames('w-100', { 'w-xxl-75': showActions })}>
                    <div className={classNames('d-flex hover-actions-trigger', { 'flex-end-center': message.type === 'sent' })}>
                        {message.type === 'received' && (
                            <Avatar
                                src={ai}
                                size="xl"
                                className="me-1 pt-3 flex-shrink-0"
                            />

                        )}

                        {message.type === 'received' && message.asking && <Spinner className="mt-3" animation="grow" />}


                        {/*message.type === 'sent' && showActions && (
                            <MessageActionButtons actions={actions} variant="sent" />
                        )*/}

                        <div className={classNames('chat-message-content me-2', { received: message.type === 'received' })}>
                            <div className={classNames('p-3', { 'bg-200 rounded-5': message.type === 'sent', })}>
                                {message.message && <p className="mb-0 fs-8">{formatText(message.message)}</p>}
                            </div>
                        </div>

                    </div>
                    <div
                        className={classNames('d-flex gap-1 fs-10', {
                            'ms-7': message.type === 'received',
                            'justify-content-end': message.type === 'sent'
                        })}
                    >
                    </div>

                </div>
            </div>
        </div>
    );
};



const ChatBotFooter = ({ externalInput }: { externalInput: string }) => {

    const { t } = useTranslation();
    const { currentConversation, sendMessage } = useChatContext();

    const [messageText, setMessageText] = useState('');

    const handleSendMessage = () => {
        if (currentConversation && messageText) {
            sendMessage(messageText);
            setMessageText('');
        }
    };


    useEffect(() => {
        console.log(externalInput);
        if (externalInput) {
            setMessageText(externalInput);
        }

    }, [externalInput]);


    return (
        <Card.Footer>
            <ReactTextareaAutosize
                minRows={1}
                maxRows={6}
                value={messageText}
                placeholder={t("Ask something...")}
                onChange={({ target: { value } }) => setMessageText(value)}
                className="chat-textarea form-control outline-none border-0 scrollbar resize-none mb-1 p-0 fs-8 fw-normal"
            />

            <div className="d-flex gap-3 align-items-center">
                <Button
                    variant="primary"
                    endIcon={<FontAwesomeIcon icon={faPaperPlane} className="ms-2" />}
                    className="ms-auto"
                    type="submit"
                    onClick={handleSendMessage}
                >
                    {t("Send")}
                </Button>
            </div>
        </Card.Footer>
    );
};



const PromptDrawer = ({ prompt, showPromptDrawer, onClosePromptDrawer }: PromptWidgetProps) => {
    const { currentConversation, startNewChat } = useChatContext();
    const [asking, setAsking] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const messageEndRef = useRef<null | HTMLSpanElement>(null);

    useEffect(() => {
        if (showPromptDrawer) {
            startNewChat(prompt);
        }

    }, [showPromptDrawer]);

    // Function to handle click on card inside SampleQuestions
    const handleClickSampleQuestion = (message: string) => {
        setInputValue(message);
    };

    return (
        <div>

            <Offcanvas show={showPromptDrawer} onHide={onClosePromptDrawer} placement='end' className="w-50">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{parse(prompt.prompt_name)}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>


                    <Card className="h-100 w-100">
                        <Card.Body className="p-3 scrollbar d-flex flex-column ">
                            <SampleQuestions questions={getFilteredQuestions(prompt)} onClickMessage={handleClickSampleQuestion} />

                            {currentConversation && currentConversation.messages.map((message, index) => (

                                <Message
                                    message={message}
                                    key={index}
                                />

                            ))}
                            <span ref={messageEndRef} />

                            {asking && <Spinner animation="grow" />}
                        </Card.Body>

                        <ChatBotFooter externalInput={inputValue} />

                    </Card>

                </Offcanvas.Body>
            </Offcanvas>

        </div >
    );
};

const PromptWidget = ({ page }: { page: string }) => {

    const { t } = useTranslation();
    // Prompt 
    const [promptList, setPromptList] = useState<PromptLight[]>([]);
    const [prompt, setPrompt] = useState<PromptLight>();
    const [showPromptDrawer, setShowPromptDrawer] = useState(false);
    const handleClosePromptDrawer = () => setShowPromptDrawer(false);

    const handleShowPromptDrawer = async (index: number) => {
        setShowPromptDrawer(true);
        setPrompt(promptList[index]);
    }

    const fetchPrompt = async () => {
        try {
            const response = await apiClient.get('/v1/prompts?page=' + page);
            setPromptList(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchPrompt();
    }, []);

    return (
        <>
            {prompt && (
                <PromptsProvider>
                    <PromptDrawer prompt={prompt} showPromptDrawer={showPromptDrawer} onClosePromptDrawer={handleClosePromptDrawer} />
                </PromptsProvider>
            )}

            {promptList.length > 0 && (
                <div className="w-100 mt-2  mb-2">
                    <Card className='border border-light rounded-5' >
                        <Card.Body>
                            <Row className="p-2" >
                                {promptList.map((prompt, index) => (
                                    <Col key={index} sm={4} md={4} lg={4}>
                                        <Card className={`border border-light rounded-5  ${prompt.background_color}`} style={{ cursor: 'pointer' }} onClick={() => handleShowPromptDrawer(index)}>
                                            <Card.Body>
                                                <div className="d-flex flex-column w-100 align-items-center" style={{ 'height': '200px' }}>

                                                    <DynamicIcon iconName={prompt.icon_name} width={100} alt={''} />

                                                    <p className="mt-2 mx-1 fs-8" style={{ textAlign: 'justify' }}>{parse(prompt.prompt_name)}</p>

                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
            )
            }
        </>
    );
};


export default PromptWidget;
